import { useQueries, UseQueryOptions } from 'react-query'
import { useParams } from 'react-router-dom'

import { getCurrentOrganizationId } from '@/components/App/selectors'
import Modal, { Size } from '@/components/Modal'
import { useRoles } from '@/components/RolesContainer'
import ButtonModal from '@/components/TailwindUI/ButtonModal'
import ModalVariantLogo from '@/components/TailwindUI/ModalVariantLogo'
import useFeatures from '@/components/useFeatures'
import { WORKSHOP_AGENTS } from '@/constants/featureToggles'
import { WorkgridCatalogDatasource, WorkgridDatasource } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { dataSourceV3CatalogQueryOptions, dataSourceV3QueryOptions } from '@/pages/SpaceAppBuilder/utilities/queries'
import { useAppSelector } from '@/store/hooks'
import cn from '@/utilities/cn'

type Props = {
  size?: Size
  variant: 'success' | 'warning' | 'error' | 'info'
  isOpen: boolean
  onConfirm: () => void
  onSecondaryConfirm?: () => void
  onCancel: () => void
  descriptor?: string
  confirmText?: string
  secondaryText?: string
  cancelText?: string
  loadingText?: string
  loading?: boolean
  disabled?: boolean
  showCancel?: boolean
  appDataSources?: Array<WorkgridDatasource>
  currentUserId?: string
}

const ConfirmDialogWithDataSources = ({
  size = 'xs',
  variant,
  isOpen,
  onConfirm,
  onSecondaryConfirm,
  onCancel,
  descriptor,
  confirmText = 'Confirm',
  secondaryText,
  cancelText = 'Cancel',
  loadingText = 'Loading...',
  loading = false,
  disabled = false,
  showCancel = true,
  appDataSources,
  currentUserId
}: Props) => {
  const { spaceId } = useParams<{ spaceId: string; appId: string }>()
  const organizationId = useAppSelector(getCurrentOrganizationId)
  const roles = useRoles()

  const hasAppOwnerRole = roles.hasWorkshopAppOwnerRole(spaceId)
  const hasAppManagerRole = roles.hasWorkshopManagerRole(spaceId)
  const hasOrgAdminRole = roles.hasOrganizationAdminRole(organizationId)

  // If the user is the app owner and not an app manager or org admin, they are removing themselves from the app and will no longer have read/write access.
  const isRemovingAccess = hasAppOwnerRole && !hasAppManagerRole && !hasOrgAdminRole
  const featureFlags = useFeatures()
  const agentsFlagOn = !!featureFlags[WORKSHOP_AGENTS]

  type WorkgridDatasourceInstanceQueries = UseQueryOptions<WorkgridDatasource>[]
  const dataSourceInstancesQuery = useQueries<WorkgridDatasourceInstanceQueries>(
    appDataSources?.map(ds => ({
      ...dataSourceV3QueryOptions({
        spaceId,
        datasourceId: ds.datasourceId,
        instanceId: ds.id
      }),
      enabled: !!appDataSources
    })) || []
  )
  type WorkgridDatasourceQueries = UseQueryOptions<WorkgridCatalogDatasource>[]
  const datasourcesQueryResult = useQueries<WorkgridDatasourceQueries>(
    appDataSources?.map(ds => ({
      ...dataSourceV3CatalogQueryOptions({ spaceId, datasourceId: ds.datasourceId })
    })) || []
  )
  return (
    <Modal size={size} isOpen={isOpen} onClose={() => onCancel()}>
      <div className="px-4 py-3">
        <div className="sm:flex sm:items-start">
          <ModalVariantLogo variant={variant} />
          <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
            <h3 className="text-base font-semibold leading-6 text-zinc-900">
              {`Are you sure you want to remove yourself from this ${
                descriptor === 'data source instance'
                  ? 'data source'
                  : descriptor === 'document'
                    ? 'document'
                    : agentsFlagOn
                      ? 'agent'
                      : 'app'
              }?`}
            </h3>
            <div className="mt-2">
              <p className="text-sm text-zinc-500">
                {isRemovingAccess ? (
                  descriptor === 'data source instance' ? (
                    <>
                      <span>You will </span> <span className="font-bold">no longer have access this datasource</span>{' '}
                      <span>and will be redirected to the datasource list page.</span>
                    </>
                  ) : descriptor === 'document' ? (
                    <>
                      <span>You will </span> <span className="font-bold">no longer have access this document</span>{' '}
                    </>
                  ) : (
                    <>
                      <span>You will </span>{' '}
                      <span className="font-bold">no longer have access this {agentsFlagOn ? 'agent' : 'app'}</span>{' '}
                      <span>and will be redirected to the {agentsFlagOn ? 'agent' : 'app'} list page.</span>
                    </>
                  )
                ) : (
                  `You will no longer have access to edit this ${
                    descriptor === 'data source instance'
                      ? 'data source'
                      : descriptor === 'document'
                        ? 'document'
                        : agentsFlagOn
                          ? 'agent'
                          : 'app'
                  }.`
                )}
              </p>
              {appDataSources &&
                appDataSources.length > 0 &&
                appDataSources.find(dataSource => dataSource.createdBy === currentUserId) && (
                  <p className="text-sm text-zinc-500">{'The data sources you added will continue to run.'}</p>
                )}
            </div>

            {dataSourceInstancesQuery?.map((dataSource, index) => {
              if (dataSource.data?.createdBy === currentUserId) {
                const datasourceDetails = datasourcesQueryResult.find(
                  ds => ds.data?.id === dataSource.data?.datasourceId
                )?.data
                return (
                  <div className="mb-4 mt-4 flex min-w-0 flex-auto space-x-4" key={index}>
                    <img className="h-10 w-10 rounded-md" src={datasourceDetails?.iconUrl} alt="datasource icon" />
                    <div className="min-w-0 flex-auto">
                      <div>
                        <h2 className="text-sm text-zinc-900">{datasourceDetails?.name}</h2>
                        <p className="text-sm text-zinc-500">{dataSource.data?.name}</p>
                      </div>
                    </div>
                  </div>
                )
              }
            })}
          </div>
        </div>

        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <ButtonModal
            disabled={disabled || loading}
            variant={variant}
            onConfirm={onConfirm}
            loadingText={loadingText}
            confirmText={confirmText}
          />
          {secondaryText && onSecondaryConfirm && (
            <ButtonModal
              disabled={disabled || loading}
              variant={variant}
              onConfirm={onSecondaryConfirm}
              loadingText={loadingText}
              confirmText={secondaryText}
            />
          )}
          {showCancel && (
            <button
              type="button"
              disabled={loading}
              className={cn(
                'mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 hover:bg-zinc-50 sm:mt-0 sm:w-auto'
              )}
              onClick={() => onCancel()}
            >
              {cancelText}
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default ConfirmDialogWithDataSources
